import { httpClient } from '@/utils/httpClient';
import { entityService } from '@/services/EntityService';

export type OrderStatus = 'Pending' | 'Partially Received' | 'Completed' | 'Cancelled';

export type PurchaseType = 'GPC' | 'Natick/MIPR' | 'Contract' | 'GPC/GSA 25K' | 'MILSTRIP';

export interface OrderListRow {
  id: string;
  entity_identifier: string | null;
  entity_data: {
    order_date?: string;
    status?: OrderStatus;
    total_items?: number;
    line_item_count?: number;
    purchase_type?: PurchaseType;
    external_status?: string;
    description?: string;
    pr_description?: string;
    document_number?: string;
    log_form_number?: number | string;
    ufr?: boolean;
    status_date?: string;
  };
  metadata?: {
    aggregates?: {
      line_item_count?: number;
      total_item_quantity?: number;
    };
  } | null;
  /** Pre-computed from orders_view — total items still awaiting receipt */
  outstanding_quantity?: number | null;
  /** Pre-computed from orders_view — sum of all ordered quantities */
  total_ordered_quantity?: number | null;
  /** Pre-computed from orders_view — sum of all received quantities */
  total_received_quantity?: number | null;
}

export interface OrderLineItemRow {
  id: string;
  entity_identifier: string | null;
  display_name: string | null;
  entity_data: {
    order_id?: string;
    catalog_id?: string;
    quantity?: number;
    unit_price_at_time_of_order?: number;
    actual_price?: number;
    contract_number?: string;
    notes?: string;
    qty_cancelled?: number;
  };
  catalog?: {
    id: string;
    display_name?: string | null;
    entity_data?: {
      nsn?: string;
      lin?: string;
      nomenclature?: string;
      size?: string;
      secondary_location_id?: string;
      primary_location_id?: string;
    };
  };
  receipts?: Array<{
    entity_data?: {
      quantity_received?: number;
      receipt_date?: string | null;
      location_id?: string | null;
    };
  }>;
}

export interface OrderLineItemViewRow {
  id: string;
  entity_identifier: string | null;
  display_name: string | null;
  entity_data: Record<string, unknown>;
  status: string;
  created_at: string;
  updated_at: string;
  order_id: string;
  catalog_id: string | null;
  catalog_display_name: string | null;
  catalog_entity_data: Record<string, unknown> | null;
  ordered_quantity: number;
  unit_price: number | null;
  nsn: string | null;
  lin: string | null;
  nomenclature: string | null;
  receipts: Array<{
    id: string;
    entity_identifier: string | null;
    quantity_received: number;
    receipt_date: string | null;
    location_id?: string | null;
    created_at: string;
  }>;
  total_received_quantity: number;
  outstanding_quantity: number;
  is_fully_received: boolean;
}

export interface OrderLineItemHistoryViewRow extends OrderLineItemViewRow {
  is_cancelled: boolean;
  cancelled_at: string | null;
  cancellation_reason: string | null;
}

export interface OrderLineItemHistoryRow extends OrderLineItemRow {
  is_cancelled: boolean;
  cancelled_at: string | null;
  cancellation_reason: string | null;
}

export interface PriceHistoryLineItem {
  catalog_id: string;
  quantity: number;
  entity_data: {
    actual_price?: number | null;
    unit_price_at_time_of_order?: number | null;
  };
}

export interface AveragePriceResult {
  avgPrice: number;
  sampleCount: number;
}

/** Convert an unknown value (number or numeric string) to a finite number, or return null. */
function toFinitePrice(value: unknown): number | null {
  if (value == null) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Calculate a quantity-weighted average price per catalog_id from a list of line items.
 * For each item, uses actual_price if present, otherwise falls back to
 * unit_price_at_time_of_order. Items with neither value are skipped.
 * The returned average is rounded to 2 decimal places.
 * sampleCount represents total units (sum of quantities) used in the weighted average.
 */
export function calculateAveragePrices(
  lineItems: PriceHistoryLineItem[],
): Map<string, AveragePriceResult> {
  const groups = new Map<string, { weightedSum: number; totalQty: number }>();

  for (const item of lineItems) {
    const price =
      toFinitePrice(item.entity_data.actual_price) ??
      toFinitePrice(item.entity_data.unit_price_at_time_of_order);
    if (price == null) continue;
    const qty = item.quantity > 0 ? item.quantity : 1;
    const existing = groups.get(item.catalog_id);
    if (existing) {
      existing.weightedSum += price * qty;
      existing.totalQty += qty;
    } else {
      groups.set(item.catalog_id, { weightedSum: price * qty, totalQty: qty });
    }
  }

  const result = new Map<string, AveragePriceResult>();
  for (const [catalogId, { weightedSum, totalQty }] of groups) {
    const avg = weightedSum / totalQty;
    result.set(catalogId, {
      avgPrice: Math.round(avg * 100) / 100,
      sampleCount: totalQty,
    });
  }
  return result;
}

class OrdersService {
  async listOrders(): Promise<OrderListRow[]> {
    return httpClient.get<OrderListRow[]>(
      `/api/rest/data/v1/orders_view?select=id,entity_identifier,entity_data,outstanding_quantity,total_ordered_quantity,total_received_quantity&order=entity_identifier.asc`,
    );
  }

  async getOrderLineItems(orderId: string): Promise<OrderLineItemRow[]> {
    // Use the existing order_line_items_view from migration 030
    const viewRows = await httpClient.get<OrderLineItemViewRow[]>(
      `/api/rest/data/v1/order_line_items_view?order_id=eq.${orderId}&order=lin.asc,nsn.asc`,
    );
    return viewRows.map((row) => this.mapLineItemViewRow(row));
  }

  async getOrderLineItemsHistory(orderId: string): Promise<OrderLineItemHistoryRow[]> {
    // is_cancelled=eq.true is required: the history view returns all rows (active
    // and cancelled). Without this filter callers would receive active items already
    // returned by getOrderLineItems, causing duplicates in the UI.
    const viewRows = await httpClient.get<OrderLineItemHistoryViewRow[]>(
      `/api/rest/data/v1/order_line_items_history_view?order_id=eq.${orderId}&is_cancelled=eq.true&order=lin.asc,nsn.asc`,
    );
    return viewRows.map((row) => ({
      ...this.mapLineItemViewRow(row),
      is_cancelled: row.is_cancelled,
      cancelled_at: row.cancelled_at,
      cancellation_reason: row.cancellation_reason,
    }));
  }

  private mapLineItemViewRow(row: OrderLineItemViewRow): OrderLineItemRow {
    const catalogEntityData = (row.catalog_entity_data ?? {}) as {
      secondary_location_id?: string | null;
      primary_location_id?: string | null;
    };
    return {
      id: row.id,
      entity_identifier: row.entity_identifier,
      display_name: row.display_name,
      entity_data: {
        order_id: row.order_id,
        catalog_id: row.catalog_id ?? undefined,
        quantity: row.ordered_quantity,
        unit_price_at_time_of_order: row.unit_price ?? undefined,
        actual_price: (row.entity_data as { actual_price?: number })?.actual_price ?? undefined,
        contract_number:
          (row.entity_data as { contract_number?: string })?.contract_number ?? undefined,
        notes: (row.entity_data as { notes?: string })?.notes ?? undefined,
        qty_cancelled:
          typeof (row.entity_data as { qty_cancelled?: unknown }).qty_cancelled === 'number'
            ? (row.entity_data as { qty_cancelled: number }).qty_cancelled
            : undefined,
      },
      catalog: row.catalog_id
        ? {
            id: row.catalog_id,
            display_name: row.catalog_display_name,
            entity_data: {
              nsn: row.nsn ?? undefined,
              lin: row.lin ?? undefined,
              nomenclature: row.nomenclature ?? undefined,
              secondary_location_id: catalogEntityData.secondary_location_id ?? undefined,
              primary_location_id: catalogEntityData.primary_location_id ?? undefined,
            },
          }
        : undefined,
      receipts: row.receipts.map((receipt) => ({
        entity_data: {
          quantity_received: receipt.quantity_received,
          receipt_date: receipt.receipt_date,
          location_id: receipt.location_id ?? null,
        },
      })),
    };
  }

  async updateOrderStatus(orderId: string, status: OrderStatus): Promise<void> {
    const order = await entityService.get({ type: 'order', id: orderId });
    if (!order) {
      throw new Error(`Order ${orderId} not found`);
    }

    await entityService.update({
      type: 'order',
      id: orderId,
      patch: {
        entity_data: {
          ...(order.entity_data ?? {}),
          status,
        },
      },
    });
  }

  /**
   * Fetch order line items for the given catalog IDs created within the last `since` date.
   * Used to compute rolling 365-day average prices for purchase requisition price proposals.
   */
  async getOrderLineItemsByCatalogIds(
    catalogIds: string[],
    since: Date,
  ): Promise<PriceHistoryLineItem[]> {
    if (catalogIds.length === 0) return [];

    const idsParam = catalogIds.join(',');
    const sinceParam = since.toISOString();

    const rows = await httpClient.get<
      Array<{
        catalog_id: string | null;
        ordered_quantity: number | null;
        entity_data: Record<string, unknown>;
      }>
    >(
      `/api/rest/data/v1/order_line_items_view?catalog_id=in.(${idsParam})&created_at=gte.${sinceParam}&select=catalog_id,ordered_quantity,entity_data`,
    );

    return rows
      .filter(
        (
          r,
        ): r is {
          catalog_id: string;
          ordered_quantity: number | null;
          entity_data: Record<string, unknown>;
        } => r.catalog_id !== null,
      )
      .map((r) => ({
        catalog_id: r.catalog_id,
        quantity: r.ordered_quantity ?? 1,
        entity_data: {
          actual_price: toFinitePrice((r.entity_data as { actual_price?: unknown }).actual_price),
          unit_price_at_time_of_order: toFinitePrice(
            (r.entity_data as { unit_price_at_time_of_order?: unknown })
              .unit_price_at_time_of_order,
          ),
        },
      }));
  }

  async checkAndUpdateOrderCompletion(orderId: string): Promise<void> {
    const lineItems = await this.getOrderLineItems(orderId);

    if (lineItems.length === 0) {
      return;
    }

    const allFullyReceived = lineItems.every((item) => {
      const ordered = item.entity_data.quantity ?? 0;
      const received =
        item.receipts?.reduce(
          (sum, receipt) => sum + (receipt.entity_data?.quantity_received ?? 0),
          0,
        ) ?? 0;
      return received >= ordered;
    });

    if (allFullyReceived) {
      await this.updateOrderStatus(orderId, 'Completed');
    } else {
      const anyReceived = lineItems.some((item) => {
        const received =
          item.receipts?.reduce(
            (sum, receipt) => sum + (receipt.entity_data?.quantity_received ?? 0),
            0,
          ) ?? 0;
        return received > 0;
      });

      if (anyReceived) {
        await this.updateOrderStatus(orderId, 'Partially Received');
      }
    }
  }
}

export const ordersService = new OrdersService();
